<script setup lang="ts">
import { PiButton, PiChip, PiTooltip } from '@pappers-fr/pappers-ui';
import type { Piece } from '@/types';

defineProps<{
	piece: Piece;
	isInserted: boolean;
	number: number | null;
	occurrenceCount: number;
}>();

defineEmits<{
	insert: [];
}>();

const pieceUrl = (piece: Piece): string => {
	if (piece.type === 'decision' && piece.detailsId) {
		return `https://justice.pappers.fr/decision/${piece.detailsId}`;
	}
	return '';
};

const formatDate = (date: string | null): string => {
	if (!date) return '';
	const d = new Date(date);
	return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
};
</script>

<template>
	<div
		class="p-3 mb-2 border rounded-lg transition-colors"
		:class="
			isInserted ? 'border-primary-200 bg-primary-50 cursor-pointer' : 'border-neutral-200 hover:border-neutral-300'
		"
	>
		<!-- Ligne 1 : Pièce n°X + (i) + Tag + bouton -->
		<div class="flex items-center justify-between">
			<div class="flex items-center gap-1.5">
				<span v-if="isInserted && number" class="text-xs font-semibold text-primary-600"> Pièce n°{{ number }} </span>
				<span v-else-if="piece.number" class="text-xs font-semibold text-neutral-500">
					Pièce n°{{ piece.number }}
				</span>
				<span v-else class="text-xs font-semibold text-neutral-400"> Pièce – </span>
				<PiTooltip :is-on-click="false" type="default" custom-class="!inline-flex items-center">
					<span
						class="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-neutral-200 text-neutral-500 text-[9px] font-semibold cursor-default leading-none"
						>i</span
					>
					<template #tooltip>
						<div class="text-xs max-w-[200px]">
							{{
								piece.number
									? "Ce numéro a été défini sur l'interface Pappers et sera toujours réservé pour cette pièce"
									: isInserted && number
										? "Ce numéro est attribué automatiquement selon l'ordre d'insertion dans le document"
										: "Cette pièce n'a pas encore été insérée et prendra un numéro automatiquement lors de son insertion"
							}}
						</div>
					</template>
				</PiTooltip>
				<span v-if="occurrenceCount > 1" class="text-xs text-primary-500">×{{ occurrenceCount }}</span>
				<PiChip
					v-if="piece.tags"
					:label="piece.tags"
					:color="piece.tags === 'Client' ? 'green' : piece.tags === 'Adversaire' ? 'red' : 'purple'"
					size="sm"
				/>
			</div>
			<button
				class="text-xs font-medium text-primary-500 hover:text-primary-600 hover:bg-primary-100 px-1.5 py-0.5 rounded"
				@click.stop="$emit('insert')"
			>
				Insérer
			</button>
		</div>

		<!-- Ligne 2-3 : Nom (2 lignes max) + date inline -->
		<p class="text-xs text-neutral-800 mt-1 line-clamp-2">
			{{ piece.name }}<span v-if="piece.date" class="text-neutral-400"> – {{ formatDate(piece.date) }}</span>
		</p>

		<!-- Lien vers le front -->
		<a
			v-if="pieceUrl(piece)"
			class="text-xs text-primary-500 hover:underline mt-1 inline-block"
			:href="pieceUrl(piece)"
			target="_blank"
			@click.stop
		>
			Voir la pièce
		</a>
	</div>
</template>
